/**
 * @file navigation.ts
 * @description Configuration file for the application's navigation system.
 * This file defines the structure and content of the sidebar navigation menu
 * used throughout the application. The navigation is organized by top-level
 * routes (the school sections, admin, settings) and their respective sections.
 *
 * Key features:
 * - Route-based navigation (different menus for different top-level routes)
 * - Collapsible sections
 * - Icon support using Lucide icons
 * - Disabled state support for menu items
 * - Support for separators between sections
 * - Configurable section collapse states
 * - Optional section titles
 *
 * Gating: who sees an item is decided in one place, `lib/navigation/resolve.ts`,
 * from the signals `lib/navigation/use-nav-signals.ts` gathers. Each nav key
 * names the surface its routes sit behind (`navigationSurfaces`, read from the
 * surface map in `lib/access/surface-map.ts`, which the layout guards also
 * read); an item narrows it with its own `surface`, `requiresArbiterConfigured`
 * or `flag`. To gate a new destination on a PostHog flag, add `flag: '<key>'` to
 * the item and the key to `NAV_FLAG_KEYS` in `lib/navigation/types.ts`.
 * `topNavConfig` (end of file) names a `surface` per link instead.
 *
 * Nav visibility is presentation, not authorization: the layouts and the
 * backend guard the routes. A hidden item's page still renders for anyone who
 * types its URL unless its layout redirects them.
 *
 * Usage:
 * ```typescript
 * import { navigationConfig } from '@/config/navigation'
 *
 * // Access navigation for a route
 * const supportNav = navigationConfig.support
 * ```
 */

import { GearIcon } from '@radix-ui/react-icons'
import type { LucideIcon } from 'lucide-react'
import {
  Bell,
  LayoutDashboard,
  Settings,
  Users,
  Send,
  Users2,
  Smartphone,
  Paintbrush,
  Database,
  Activity,
  FileSearch,
  ListTree,
  UsersRound,
  Home,
  CalendarClock,
  GraduationCap,
  Flag,
  Shell,
  User,
  FileText,
  Megaphone,
  CreditCard,
  Key,
  Webhook,
  UserCog,
  BellRing,
  Gauge,
  BookOpen,
  Newspaper,
  Heart,
  Building2,
  UserCheck,
  BarChart3,
  MonitorPlay,
  Menu,
  ArrowLeftRight,
  Rocket,
  Sparkles,
  Award,
  Folders,
  ClipboardList,
  Clock,
  IdCard,
  LayoutGrid,
  ToggleRight,
  Trophy,
  Crosshair,
  FlaskConical,
  MessageSquare,
  Inbox,
  Mail,
  ListChecks,
  TrendingUp,
  ShieldAlert,
  Bot,
  Presentation,
  ScrollText,
  Phone
} from 'lucide-react'
import type { SurfaceKey } from '@/lib/access/surface-map'
import type { NavFlagKey, TopNavItem } from '@/lib/navigation/types'

/**
 * Represents a single navigation menu item
 * @interface NavItem
 * @property {string} title - Display text for the menu item
 * @property {string} url - Target URL for the menu item
 * @property {LucideIcon} icon - Lucide icon component to display
 * @property {boolean} [disabled] - Optional flag to disable the menu item
 *
 * Gates (`surface`, `requiresArbiterConfigured`, `requiresCallingOn`, `flag`) only narrow the nav
 * key's surface; `lib/navigation/resolve.ts` applies them. None of them is
 * authorization: the surface's own guard protects the route.
 */
export interface NavItem {
  title: string
  url: string
  icon: LucideIcon
  disabled?: boolean
  /**
   * The narrower surface the item's route sits behind (for example `skylab`
   * under `school-manage`). Hidden from viewers that surface turns away.
   */
  surface?: SurfaceKey
  /**
   * Hides the item until the per-school Arbiter binding is seeded.
   * Resolved at render time via `useArbiterConfigured`. Schools without
   * a binding never see the item; the page itself still serves a
   * "Not configured" screen if a super-admin types the URL.
   */
  requiresArbiterConfigured?: boolean
  /**
   * Hides the item unless the active school has the paid calling module on
   * (its own `globalFeatures.call.enabled`). The pages still render for anyone
   * who types the URL; they simply list no calls.
   */
  requiresCallingOn?: boolean
  /**
   * The PostHog flag that must be `true` for the item to show. Preview flags
   * default OFF and are enabled per school (they target the `school` group; the
   * dashboard calls `posthog.group('school', id)` in
   * `interfaces/posthog/tracking/school-group.tsx`). Nav visibility only, not
   * authz: the route still renders for anyone who types the URL.
   */
  flag?: NavFlagKey
}

/**
 * Represents a section in the navigation menu
 * @interface NavSection
 * @property {string} title - Section title
 * @property {NavItem[]} items - Array of navigation items in this section
 * @property {'open' | 'closed' | 'none'} [collapse] - Collapse state of the section
 *    - 'open': Section starts expanded
 *    - 'closed': Section starts collapsed
 *    - 'none': Section is not collapsible
 * @property {boolean} [hideTitle] - Whether to hide the section title
 * @property {boolean} [separator] - Whether to show a separator above this section
 */
export interface NavSection {
  title: string
  items: NavItem[]
  collapse?: 'open' | 'closed' | 'none' // Default is 'open' if not specified
  hideTitle?: boolean // When true, title won't be shown
  separator?: boolean // When true, shows a separator above this section
}

/**
 * Type definition for the entire navigation configuration
 * Organized by top-level routes (e.g., school, admin, demo, settings)
 * @type NavigationData
 */
export type NavigationData = {
  [key: string]: NavSection[]
}

/**
 * Main navigation configuration object
 * Contains all navigation menus organized by top-level routes
 *
 * Structure:
 * - manage / comms / events / support: the four school sections
 * - admin: Super admin functionality
 * - settings: User and system settings
 *
 * Each route contains an array of sections, each section contains
 * an array of menu items with their respective properties.
 */
export const navigationConfig: NavigationData = {
  // ==================== NEW SECTIONS FOR SCHOOL SEPARATION ====================
  manage: [
    {
      title: 'Manage App',
      items: [
        {
          title: 'Dashboard',
          url: '/school/manage',
          icon: LayoutDashboard
        },
        {
          title: 'App Users',
          url: '/school/manage/users',
          icon: Users
        },
        {
          title: 'Groups',
          url: '/school/manage/groups',
          icon: Users2
        },
        {
          title: 'Sections',
          url: '/school/manage/sections',
          icon: BookOpen
        },
        {
          title: 'Calls',
          url: '/school/manage/calls',
          icon: Phone,
          requiresCallingOn: true
        }
      ],
      collapse: 'open',
      hideTitle: true
    },
    {
      title: 'Mobile App',
      items: [
        {
          title: 'Features',
          url: '/school/manage/features',
          icon: Sparkles
        },
        {
          title: 'Design',
          url: '/school/manage/design',
          icon: Paintbrush
        },
        {
          title: 'Menus',
          url: '/school/manage/menus',
          icon: Menu
        },
        {
          title: 'Pages',
          url: '/school/manage/pages',
          icon: LayoutGrid,
          flag: 'school_admin_manage_pages'
        }
      ],
      collapse: 'none'
    },
    {
      title: 'Platform Data',
      items: [
        {
          title: 'People Explorer',
          url: '/school/manage/explorer/people',
          icon: Users2
        },
        {
          title: 'Sections Explorer',
          url: '/school/manage/explorer/sections',
          icon: BookOpen
        },
        {
          title: 'Assignments Explorer',
          url: '/school/manage/explorer/assignments',
          icon: FileText
        },
        {
          title: 'Lists Explorer',
          url: '/school/manage/explorer/lists',
          icon: ListTree,
          surface: 'lists-explorer'
        },
        {
          title: 'Admissions',
          url: '/school/manage/admissions',
          icon: GraduationCap,
          flag: 'school_admin_admissions'
        },
        {
          title: 'Athletics',
          url: '/school/manage/athletics',
          icon: Trophy,
          flag: 'athletics_hub_web'
        }
        // {
        //   title: 'Groups Explorer',
        //   url: '/school/manage/explorer/groups',
        //   icon: UsersRound,
        // },
        // {
        //   title: 'Platform Config',
        //   url: '/school/manage/explorer/platform-config',
        //   icon: Settings,
        // },
        // {
        //   title: 'Calendar Events',
        //   url: '/school/manage/explorer/calendar-events',
        //   icon: CalendarClock,
        // },
      ],
      collapse: 'none'
    },
    {
      title: 'Settings',
      items: [
        {
          title: 'School Settings',
          url: '/school/manage/settings',
          icon: Settings
        }
      ],
      collapse: 'none'
    },
    {
      title: 'Integrations',
      items: [
        {
          title: 'Arbiter Sports',
          url: '/school/manage/integrations/arbiter',
          icon: Trophy,
          requiresArbiterConfigured: true
        }
      ],
      collapse: 'none'
    },
    {
      title: 'Dev Tools',
      items: [
        {
          title: 'Sky API Explorer',
          url: '/school/manage/explorer',
          icon: FileSearch
        },
        {
          title: 'User Explorer',
          url: '/school/manage/explorer/user-explorer',
          icon: User
        },
        {
          title: 'Skylab',
          url: '/school/manage/skylab',
          icon: FlaskConical,
          surface: 'skylab'
        }
        // {
        //   title: 'Session',
        //   url: '/school/manage/session',
        //   icon: Shell,
        // },
        // {
        //   title: 'School Feeds Test',
        //   url: '/school/feeds',
        //   icon: Gauge,
        // },
      ],
      separator: true,
      collapse: 'closed'
    }
  ],
  comms: [
    {
      title: 'Communications',
      items: [
        {
          title: 'Overview',
          url: '/school/comms',
          icon: LayoutDashboard
        }
      ],
      collapse: 'open',
      hideTitle: true
    },
    {
      title: 'Content Tools',
      items: [
        {
          title: 'Content',
          url: '/school/comms/content',
          icon: FileText
        },
        {
          title: 'Categories',
          url: '/school/comms/content-categories',
          icon: Folders
        },
        {
          title: 'Media',
          url: '/school/comms/media',
          icon: MonitorPlay
        }
      ],
      collapse: 'none'
    },
    {
      title: 'Publishing',
      items: [
        {
          title: 'App Home',
          url: '/school/comms/app-home',
          icon: Home
        },
        {
          title: 'Broadcasts',
          url: '/school/comms/broadcasts',
          icon: Megaphone
        },
        {
          title: 'Forms',
          url: '/school/comms/forms',
          icon: ClipboardList
        },
        {
          title: 'Settings',
          url: '/school/comms/settings/publishing',
          icon: Settings
        }
      ],
      collapse: 'none'
    },
    {
      title: 'Moderation',
      items: [
        {
          title: 'Messaging Channels',
          url: '/school/comms/messaging/channels',
          icon: Sparkles
        },
        {
          title: 'All School Feeds',
          url: '/school/comms/feeds',
          icon: Send
        }
      ],
      collapse: 'none'
    }
    // Incidents — HIDDEN 2026-08-06. The Incident Management POC pages still
    // exist at /school/comms/incidents (super-admin only, page-level redirect
    // enforces it); only the nav entry is withdrawn. Restore this group to
    // bring the link back.
    // {
    //   title: 'Incidents',
    //   items: [
    //     {
    //       title: 'Incident Management',
    //       url: '/school/comms/incidents',
    //       icon: ShieldAlert,
    //       // POC — visible to platform staff only. Page-level redirect also enforces.
    //       surface: 'incidents'
    //     }
    //   ],
    //   collapse: 'none'
    // }
  ],
  events: [
    {
      title: 'Events',
      items: [
        {
          title: 'All events',
          url: '/school/events',
          icon: CalendarClock
        },
        {
          title: 'Event Collections',
          url: '/school/events/collections',
          icon: Folders
        },
        {
          title: 'Organizers',
          url: '/school/events/organizers',
          icon: Building2
        }
      ],
      collapse: 'open',
      hideTitle: true
    },
    {
      title: 'Tickets',
      items: [
        {
          title: 'Orders',
          url: '/school/events/orders',
          icon: ClipboardList
        }
      ],
      collapse: 'open'
    },
    {
      title: 'Financials',
      items: [
        {
          title: 'Payouts',
          url: '/school/events/payouts',
          icon: CreditCard,
          disabled: true
        },
        {
          title: 'Refunds',
          url: '/school/events/refunds',
          icon: ArrowLeftRight,
          disabled: true
        },
        {
          title: 'Reports',
          url: '/school/events/reports',
          icon: BarChart3,
          disabled: true
        }
      ],
      collapse: 'open'
    },
    {
      title: 'Settings',
      items: [
        {
          title: 'Event settings',
          url: '/school/events/settings',
          icon: Settings,
          disabled: true
        }
      ],
      collapse: 'open'
    }
  ],
  support: [
    {
      title: 'Support',
      items: [
        {
          title: 'Feedback',
          url: '/school/support',
          icon: MessageSquare
        },
        {
          title: 'Inbox',
          url: '/school/support/inbox',
          icon: Inbox
        }
        // Future tabs (Tickets, Roadmap) land here. Roadmap may instead
        // graduate to a more top-level surface — see the support page doc.
      ],
      collapse: 'open',
      hideTitle: true
    }
  ],
  // ==================== END NEW SECTIONS ====================
  admin: [
    {
      title: 'Admin',
      items: [
        {
          title: 'Dashboard',
          url: '/admin/dashboard',
          icon: LayoutDashboard
        },
        {
          title: 'Schools',
          url: '/admin/schools',
          icon: Building2
        },
        {
          title: 'Users',
          url: '/admin/users',
          icon: UserCheck
        },
        {
          title: 'Analytics',
          url: '#',
          icon: BarChart3
        }
      ],
      collapse: 'open'
    },
    {
      title: 'Operations',
      items: [
        {
          title: 'Mission Control',
          url: '/admin/system/mission-control',
          icon: Rocket
        },
        {
          title: 'Scheduled Tasks',
          url: '/admin/system/scheduled-tasks',
          icon: Clock
        },
        {
          title: 'Activity',
          url: '/admin/system/activity',
          icon: Activity
        },
        {
          title: 'Calls',
          url: '/admin/system/calls',
          icon: Phone
        }
      ],
      separator: false,
      collapse: 'open'
    },
    {
      title: 'Growth',
      items: [
        {
          title: 'Hunting',
          url: '/admin/hunting',
          icon: Crosshair
        },
        {
          title: 'Blog',
          url: '/admin/blog',
          icon: Newspaper
        },
        {
          title: 'Documentation',
          url: '/admin/docs',
          icon: BookOpen
        },
        {
          title: 'App Demo',
          url: '/admin/demo',
          icon: Presentation
        }
      ],
      separator: false,
      collapse: 'open'
    },
    {
      title: 'Mobile App Fleet',
      items: [
        {
          // First, because it is the entry point that answers "does anything need
          // me" before the operator has to pick which of the screens below to open.
          title: 'Overview',
          url: '/admin/system/fleet',
          icon: LayoutDashboard
        },
        {
          title: 'Apps',
          url: '/admin/system/mobile-apps',
          icon: Smartphone
        },
        {
          title: 'Publishing Accounts',
          url: '/admin/system/publishing-accounts',
          icon: IdCard
        },
        {
          title: 'Provisioning Runs',
          url: '/admin/system/mobile-apps/provisioning-runs',
          icon: ListChecks
        },
        {
          title: 'Releases',
          url: '/admin/system/releases',
          icon: Rocket
        },
        {
          title: 'Release notes',
          url: '/admin/system/release-notes',
          icon: ScrollText
        },
        {
          title: 'Adoption',
          url: '/admin/system/adoption',
          icon: TrendingUp
        },
        {
          // Renamed with U7: the screen now holds store rejections awaiting a remedy
          // alongside blocked and refused release targets, and a nav label naming only
          // half of it sends nobody to the half they are looking for.
          title: 'Needs a human',
          url: '/admin/system/attention',
          icon: ShieldAlert
        },
        {
          // The fleet-wide stream. Every other entry above shows one slice of what
          // the stores and the pipeline did; this is the one place it reads as a
          // single timeline across every app, account and release.
          title: 'Activity',
          url: '/admin/system/mobile-apps/activity',
          icon: Activity
        }
      ],
      separator: false,
      collapse: 'open'
    },
    {
      title: 'App Settings',
      items: [
        {
          title: 'App Configuration',
          url: '/admin/system/app-configuration',
          icon: Settings
        },
        {
          title: 'App Menus',
          url: '/admin/system/app-menus',
          icon: Menu
        },
        {
          title: 'AI Tools',
          url: '/admin/ai-tools',
          icon: Sparkles
        }
      ],
      separator: false,
      collapse: 'open'
    },
    {
      title: 'Platform Sync',
      items: [
        {
          title: 'Sync Jobs',
          url: '/admin/system/sync-jobs',
          icon: ArrowLeftRight
        },
        {
          title: 'Sync Queues',
          url: '/admin/system/sync-queues',
          icon: Database
        },
        {
          title: 'Default Sync Configs',
          url: '/admin/system/sync-config',
          icon: Settings
        }
      ],
      separator: false,
      collapse: 'open'
    },
    {
      // The agent-run ledger's operator surface. Its own section rather than a
      // row under Platform Sync: a run is a unit of OUR engineering work, not a
      // school's data, and filing it beside the sync jobs would read as one more
      // thing that happens to a school.
      title: 'Agents',
      items: [
        {
          title: 'Agent Runs',
          url: '/admin/system/agent-runs',
          icon: Bot
        }
      ],
      separator: false,
      collapse: 'open'
    },
    {
      title: 'Debug Tools',
      items: [
        {
          title: 'User Explorer',
          url: '/admin/user-explorer',
          icon: Users
        },
        {
          title: 'Content API Test',
          url: '/admin/testing-tools/content-explorer',
          icon: FileSearch
        },
        {
          title: 'Emit Events',
          url: '/admin/testing-tools/emit-events',
          icon: Bell
        },
        {
          title: 'Push Notification Diagnostic',
          url: '/admin/testing-tools/push-notifications',
          icon: BellRing
        },
        {
          title: 'Email Notification Tester',
          url: '/admin/testing-tools/notifications',
          icon: Mail
        }
      ],
      separator: true,
      collapse: 'open'
    },
    {
      title: 'Rewards',
      items: [
        {
          title: 'Journeys',
          url: '/admin/rewards/journeys',
          icon: Award
        }
      ],
      separator: false,
      collapse: 'open'
    }
  ],
  settings: [
    {
      title: 'Personal',
      items: [
        {
          title: 'Profile Settings',
          url: '/settings/profile',
          icon: UserCog
        },
        {
          title: 'Notifications',
          url: '/settings/notifications',
          icon: BellRing
        },
        {
          title: 'Preferences',
          url: '#',
          icon: Settings
        }
      ],
      collapse: 'open'
    },
    {
      title: 'Account',
      items: [
        {
          title: 'Account Settings',
          url: '#',
          icon: User
        },
        {
          title: 'Billing',
          url: '#',
          icon: CreditCard
        },
        {
          title: 'Usage & Analytics',
          url: '#',
          icon: Gauge
        }
      ],
      collapse: 'open'
    },
    {
      title: 'Developer',
      items: [
        {
          title: 'Integrations',
          url: '/settings/integrations',
          icon: Database,
          disabled: false
        },
        {
          title: 'API Keys',
          url: '/settings/api-keys',
          icon: Key,
          disabled: true
        },
        {
          title: 'Webhooks',
          url: '/settings/webhooks',
          icon: Webhook,
          disabled: true
        },
        {
          title: 'API Documentation',
          url: '/settings/api-docs',
          icon: FileText,
          disabled: true
        }
      ],
      collapse: 'closed'
    }
  ]
}

/**
 * The surface each `navigationConfig` key's routes sit behind, or null for an
 * ungated key. `events` and `support` have no layout of their own: the
 * `/school/*` layout gates them.
 */
export const navigationSurfaces: Record<string, SurfaceKey | null> = {
  manage: 'school-manage',
  comms: 'school-comms',
  events: 'school',
  support: 'school',
  admin: 'admin',
  settings: null
}

/**
 * Top-nav links, in render order. Each names the surface its route sits behind
 * (none for an ungated link) and, optionally, a nav-only requirement and the
 * PostHog flag that must be `true` for it to show.
 * Visibility is resolved by `visibleTopNav` in `@/lib/navigation/resolve`.
 */
export const topNavConfig: TopNavItem[] = [
  {
    title: 'Home',
    href: '/home'
  },
  {
    title: 'Communications',
    href: '/school/comms',
    surface: 'school-comms'
  },
  {
    title: 'Manage App',
    href: '/school/manage',
    surface: 'school-manage'
  },
  {
    title: 'Events',
    href: '/school/events',
    surface: 'school'
  },
  {
    title: 'Support',
    href: '/school/support',
    surface: 'school'
  },
  {
    // Visibility only — real authz is on the backend reads each tab makes
    // (/v1/athletics-hub, /v1/synced-events, /v1/athletics-availability/*).
    // Feature flag `athletics_hub_web` (PostHog) gates the one Athletics page.
    title: 'Athletics',
    href: '/school/manage/athletics',
    surface: 'school-manage',
    flag: 'athletics_hub_web'
  },
  {
    title: 'Docs',
    href: '/docs',
    // `/docs` opens to any signed-in viewer; only the link is narrowed (KTD9).
    navRequires: { school: 'view_school_admin_dashboard' },
    flag: 'docs_navigation'
  },
  {
    title: 'Admin',
    href: '/admin/dashboard',
    surface: 'admin'
  },
  {
    title: 'Map',
    href: '/admin/map',
    surface: 'admin'
  }
]
